import MainLayout from '@/common/components/layouts/MainLayout';
import FeedbackPage from '@/features/performance/feedback/pages/FeedbackPage';
import { Loader } from '@mantine/core';
import { useAutoSessionCheck } from '@tickitacka/shared';
import React, { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import {
    Navigate,
    NavigationType,
    Route,
    Routes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import ErrorBoundary from '../ErrorBoundary';

// 루트('/') 진입 가드: works 서브도메인만 앱/로그인으로 보내고,
// 그 외(orblit.space)는 자식(통합 랜딩)을 그대로 렌더한다.
const RootRedirect = ({ children }: { children: React.ReactNode }) => {
    const { user, isLoading } = useAuthStore();
    const isWorksSubdomain = window.location.hostname === 'works.orblit.space';

    if (isLoading) {
        return <LoadingSpinner />;
    }

    // works.orblit.space인 경우 → 로그인 시 앱(/planner), 비로그인 시 로그인 페이지로
    if (isWorksSubdomain) {
        return user ? (
            <Navigate to="/planner" replace />
        ) : (
            <Navigate to="/auth/login" replace />
        );
    }

    // orblit.space인 경우 → 통합 랜딩(children)을 그대로 표시
    return <>{children}</>;
};

// Lazy load components

// Landing Orblit pages
const LandingOrblitLayout = lazy(
    () => import('../../features/landing_orblit/components/LandingOrblitLayout')
);
const LandingOrblitHome = lazy(
    () => import('../../features/landing_orblit/pages/HomePage')
);
const LandingOrblitWhy1on1 = lazy(
    () => import('../../features/landing_orblit/pages/Why1on1Page')
);
const LandingOrblitCommunication = lazy(
    () => import('../../features/landing_orblit/pages/CommunicationPage')
);
const LandingOrblitGrowth = lazy(
    () => import('../../features/landing_orblit/pages/GrowthPage')
);
const LandingOrblitChange = lazy(
    () => import('../../features/landing_orblit/pages/ChangeManagementPage')
);
const LandingOrblitCompany = lazy(
    () => import('../../features/landing_orblit/pages/CompanyPage')
);
const LandingOrblitPricing = lazy(
    () => import('../../features/landing_orblit/pages/PricingPage')
);

const LoginPage = lazy(() => import('../../features/auth/pages/LoginPage'));
const SignUpPage = lazy(() => import('../../features/auth/pages/SignUpPage'));
const AuthCallback = lazy(
    () => import('../../features/auth/pages/AuthCallbackPage')
);
const PlannerPage = lazy(
    () => import('../../features/planner/pages/PlannerPage')
);
const NotePage = lazy(() => import('../../features/note/pages/NotePage'));
const GoalListPage = lazy(() => import('@/features/goal/pages/GoalListPage'));
const GoalDetailPage = lazy(
    () => import('@/features/goal/pages/GoalDetailPage')
);
const TeamSettingPage = lazy(
    () => import('@/features/team/pages/TeamSettingPage')
);
const ProjectListPage = lazy(
    () => import('@/features/project/pages/ProjectListPage')
);
const ProjectDetailPage = lazy(
    () => import('@/features/project/pages/ProjectDetailPage')
);
const MemberInvitePage = lazy(
    () => import('@/features/member_invitation/pages/MemberInvitationPage')
);
const UserSettingPage = lazy(
    () => import('@/features/auth/pages/UserSettingPage')
);
const TeamMemberPage = lazy(
    () => import('@/features/team_member/pages/TeamMemberPage')
);
const TeamMemberDetailPage = lazy(
    () => import('@/features/team_member/pages/TeamMemberDetailPage')
);
const WorkspaceSettingPage = lazy(
    () => import('@/features/workspace/pages/WorkspaceSettingPage')
);
const PersonalReportPage = lazy(
    () =>
        import(
            '@/features/performance/report/personalreport/pages/PersonalReportPage'
        )
);
const ReportDetailPage = lazy(
    () => import('@/features/performance/report/shared/pages/ReportDetailPage')
);
const TeamReportPage = lazy(
    () =>
        import('@/features/performance/report/teamreport/pages/TeamReportPage')
);
const ReportWritePage = lazy(
    () => import('@/features/performance/report/shared/pages/ReportWritePage')
);
const DrivePage = lazy(() => import('@/features/drive/pages/DrivePage'));

// 단축 URL 리다이렉션 컴포넌트 추가
const ShortUrlRedirect = lazy(() =>
    import('@/features/note/ai/ActionBar/Sharelink').then(module => ({
        default: () => <module.ShortUrlRedirect />,
    }))
);

const NotFoundPage = lazy(
    () => import('@/common/components/errorPages/NotFoundPage')
);

// 향상된 스크롤 관리 시스템
export function HandleScroll() {
    const { pathname } = useLocation();
    const navigationType = useNavigationType();
    const prevPathRef = useRef(pathname);
    const isRestoringScroll = useRef(false);

    // 주요 스크롤 컨테이너 찾기 함수
    const findScrollableContainer = useCallback(() => {
        // 스크롤 가능한 컨테이너 후보 우선순위대로 검색
        const containerSelectors = [
            '.mantine-AppShell-main',
            '[class*="mantine-AppShell-body"]',
            '[class*="content-wrapper"]',
            '[style*="overflow: auto"]',
            '[style*="overflow-y: auto"]',
            '[style*="overflow:auto"]',
            '[style*="overflow-y:auto"]',
            'main',
            '#root > div',
            'div[style*="height:"][style*="overflow"]',
        ];

        // 우선순위에 따라 순차적으로 검색
        for (const selector of containerSelectors) {
            const elements = document.querySelectorAll(selector);

            for (const element of elements) {
                if (
                    element instanceof HTMLElement &&
                    (element.scrollHeight > element.clientHeight ||
                        window.getComputedStyle(element).overflowY === 'auto' ||
                        window.getComputedStyle(element).overflowY === 'scroll')
                ) {
                    return element;
                }
            }
        }

        // 마지막 대안으로 document.body, document.documentElement 검사
        if (document.body.scrollHeight > document.body.clientHeight) {
            return document.body;
        }

        if (
            document.documentElement.scrollHeight >
            document.documentElement.clientHeight
        ) {
            return document.documentElement;
        }

        return null;
    }, []);

    // 스크롤 위치 저장 함수
    const saveScrollPosition = useCallback(() => {
        // 스크롤 복원 중에는 저장하지 않음
        if (isRestoringScroll.current) return;

        const container = findScrollableContainer();
        const scrollPosition = container ? container.scrollTop : window.scrollY;

        if (scrollPosition > 0) {
            sessionStorage.setItem(
                `scroll_${pathname}`,
                String(scrollPosition)
            );
        } else {
            sessionStorage.removeItem(`scroll_${pathname}`);
        }
    }, [pathname, findScrollableContainer]);

    // 스크롤 위치 복원 함수 - 뒤로가기 시 사용
    const restoreScrollPosition = useCallback(
        (isBackNavigation = false) => {
            // 뒤로가기 액션이 아니면 스크롤 복원하지 않음
            if (!isBackNavigation) return;

            const savedPosition = sessionStorage.getItem(`scroll_${pathname}`);
            if (!savedPosition) return;

            const position = parseInt(savedPosition, 10);
            if (isNaN(position) || position <= 0) return;

            // 스크롤 복원 상태 표시
            isRestoringScroll.current = true;

            // 스크롤 포지션 저장 및 파일 로딩 전 빠른 스크롤 고정 적용
            const preventInitialScroll = () => {
                const container = findScrollableContainer();
                if (container) {
                    container.style.scrollBehavior = 'auto';
                    container.scrollTop = position;
                } else {
                    document.documentElement.style.scrollBehavior = 'auto';
                    document.body.style.scrollBehavior = 'auto';
                    window.scrollTo(0, position);
                }
            };

            // 즉시 실행하여 초기 스크롤 방지
            preventInitialScroll();

            // DOM이 완전히 준비된 후 정확한 포지션에 스크롤 적용
            const applyFinalScroll = () => {
                const container = findScrollableContainer();

                if (container) {
                    container.style.scrollBehavior = 'auto';
                    container.scrollTop = position;

                    // 스크롤 동작 완료 후 스타일 복원
                    setTimeout(() => {
                        container.style.scrollBehavior = '';
                        isRestoringScroll.current = false;
                    }, 50);
                } else {
                    document.documentElement.style.scrollBehavior = 'auto';
                    document.body.style.scrollBehavior = 'auto';
                    window.scrollTo(0, position);

                    // 스크롤 동작 완료 후 스타일 복원
                    setTimeout(() => {
                        document.documentElement.style.scrollBehavior = '';
                        document.body.style.scrollBehavior = '';
                        isRestoringScroll.current = false;
                    }, 50);
                }
            };

            // 렌더링 완료 대기 후 최종 스크롤 위치 적용
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    applyFinalScroll();
                });
            });
        },
        [pathname, findScrollableContainer]
    );

    // 일반 네비게이션(버튼 클릭 등)시만 스크롤 초기화
    const resetScroll = useCallback(() => {
        // 뒤로가기 네비게이션이 아닐 때만 초기화
        if (navigationType === NavigationType.Pop) return;

        const container = findScrollableContainer();

        if (container) {
            container.scrollTop = 0;
        } else {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }
    }, [navigationType, findScrollableContainer]);

    // 스크롤 이벤트 처리를 위한 effect
    useEffect(() => {
        // 네비게이션 타입이 바뀌면 해당 동작 수행
        // if (prevPathRef.current !== pathname) {
        const isBackNavigation = navigationType === NavigationType.Pop;
        if (isBackNavigation) {
            // 뒤로가기 시에는 스크롤 위치 복원
            restoreScrollPosition(true);
        } else {
            // 일반 네비게이션에는 스크롤 초기화
            resetScroll();
        }
        prevPathRef.current = pathname;
        // }

        // 스크롤 이벤트 리스너 설정
        let scrollTimeout: number | null = null;
        const throttledScrollHandler = () => {
            if (scrollTimeout === null) {
                scrollTimeout = window.setTimeout(() => {
                    saveScrollPosition();
                    scrollTimeout = null;
                }, 200); // 스로틀링 시간
            }
        };

        // 글로벌 스크롤 리스너
        window.addEventListener('scroll', throttledScrollHandler, {
            passive: true,
        });

        // beforeunload 이벤트에 스크롤 위치 저장
        window.addEventListener('beforeunload', saveScrollPosition);

        // 스크롤 가능한 컨테이너에 추가 리스너 등록
        const container = findScrollableContainer();
        if (container) {
            container.addEventListener('scroll', throttledScrollHandler, {
                passive: true,
            });
        }

        return () => {
            window.removeEventListener('scroll', throttledScrollHandler);
            window.removeEventListener('beforeunload', saveScrollPosition);

            if (container) {
                container.removeEventListener('scroll', throttledScrollHandler);
            }

            if (scrollTimeout) {
                window.clearTimeout(scrollTimeout);
            }
        };
    }, [
        pathname,
        navigationType,
        saveScrollPosition,
        restoreScrollPosition,
        resetScroll,
        findScrollableContainer,
    ]);

    return null;
}

// Loading component
const LoadingSpinner = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
        }}
    >
        <Loader size="lg" />
    </div>
);

// Route configurations
type LayoutType = 'main' | 'team';

interface ProtectedRoute {
    path: string;
    Component: React.ComponentType;
    layout: LayoutType;
}

interface PublicRoute {
    path: string;
    Component: React.ComponentType;
}

// Landing Orblit 라우트 (LandingOrblitLayout 적용)
const landingOrblitRoutes: PublicRoute[] = [
    { path: '/why', Component: LandingOrblitWhy1on1 },
    { path: '/why/communication', Component: LandingOrblitCommunication },
    { path: '/why/growth', Component: LandingOrblitGrowth },
    { path: '/why/change', Component: LandingOrblitChange },
    { path: '/company', Component: LandingOrblitCompany },
    { path: '/pricing', Component: LandingOrblitPricing },
];

const publicRoutes: PublicRoute[] = [
    { path: '/auth/login', Component: LoginPage },
    { path: '/auth/signup', Component: SignUpPage },
    { path: '/auth/callback', Component: AuthCallback },
    { path: '/invite/:inviteId', Component: MemberInvitePage },
    // 단축 URL 경로 추가 - 인증 필요 없음
    { path: '/s/:shortCode', Component: ShortUrlRedirect },
];

const protectedRoutes: ProtectedRoute[] = [
    {
        path: '/planner',
        Component: PlannerPage,
        layout: 'main',
    },
    {
        path: '/drive',
        Component: DrivePage,
        layout: 'main',
    },
    {
        path: '/team/:id/setting',
        Component: TeamSettingPage,
        layout: 'team',
    },
    {
        path: '/note/new',
        Component: NotePage,
        layout: 'main',
    },
    {
        path: '/note/:noteId',
        Component: NotePage,
        layout: 'main',
    },
    {
        path: '/team/:teamId/goal',
        Component: GoalListPage,
        layout: 'main',
    },
    {
        path: '/goal/:goalId',
        Component: GoalDetailPage,
        layout: 'main',
    },
    {
        path: '/team/:teamId/project',
        Component: ProjectListPage,
        layout: 'main',
    },
    {
        path: '/project/:projectId',
        Component: ProjectDetailPage,
        layout: 'main',
    },
    {
        path: '/auth/setting',
        Component: UserSettingPage,
        layout: 'main',
    },
    {
        path: '/team/:id/member',
        Component: TeamMemberPage,
        layout: 'main',
    },
    {
        path: '/team/:id/member/:memberId',
        Component: TeamMemberDetailPage,
        layout: 'main',
    },
    {
        path: '/workspace/:id/setting',
        Component: WorkspaceSettingPage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/report/personal',
        Component: PersonalReportPage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/report/personal/:reportId',
        Component: ReportDetailPage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/report/team',
        Component: TeamReportPage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/report/personal/new',
        Component: ReportWritePage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/report/team/new',
        Component: ReportWritePage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/report/team/:reportId',
        Component: ReportDetailPage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/report/:reportId',
        Component: ReportDetailPage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/feedback',
        Component: FeedbackPage,
        layout: 'main',
    },
    {
        path: '/workspace/:workspaceId/feedback/:feedbackId',
        Component: FeedbackPage,
        layout: 'main',
    },

    // 이전 URL 형식에 대한 리다이렉션
    {
        path: '/goal',
        Component: GoalListPage,
        layout: 'main',
    },
    {
        path: '/project',
        Component: ProjectListPage,
        layout: 'main',
    },
];

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, isLoading } = useAuthStore();
    const location = useLocation();
    const isInvitePath = location.pathname.startsWith('/invite/');
    const { checkAndRedirect } = useAutoSessionCheck();

    // 컴포넌트 마운트 시 세션 유효성 검사
    useEffect(() => {
        if (user && !isLoading) {
            void checkAndRedirect();
        }
    }, [user, isLoading, checkAndRedirect]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    // 초대 링크인 경우 로그인 페이지로 리다이렉트하면서 원래 경로 저장
    if (!user && isInvitePath) {
        return (
            <Navigate
                to="/auth/login"
                state={{ from: location.pathname }}
                replace
            />
        );
    }

    if (!user) {
        return <Navigate to="/auth/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
};

// Layout wrapper
const LayoutWrapper = ({
    children,
    layout,
}: {
    children: React.ReactNode;
    layout: 'main' | 'team';
}) => {
    // if (layout === 'team') {
    //     return <TeamLayout>{children}</TeamLayout>;
    // }
    return <MainLayout>{children}</MainLayout>;
};

const Router = () => {
    return (
        <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    {/* Landing Orblit Routes */}
                    <Route element={<LandingOrblitLayout />}>
                        <Route
                            path="/"
                            element={
                                <RootRedirect>
                                    <LandingOrblitHome />
                                </RootRedirect>
                            }
                        />
                        {landingOrblitRoutes.map(({ path, Component }) => (
                            <Route
                                key={path}
                                path={path}
                                element={<Component />}
                            />
                        ))}
                    </Route>

                    {/* Public Routes */}
                    {publicRoutes.map(({ path, Component }) => (
                        <Route key={path} path={path} element={<Component />} />
                    ))}

                    {/* Protected Routes */}
                    {protectedRoutes.map(({ path, Component, layout }) => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                <ProtectedRoute>
                                    <LayoutWrapper layout={layout}>
                                        <Component />
                                    </LayoutWrapper>
                                </ProtectedRoute>
                            }
                        />
                    ))}

                    {/* 404 - 매칭되지 않는 모든 경로 */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );
};

export default Router;
